import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { trackEvent } from '@Utilities/marketingTags'
import CTASection from '../Components/CTASection/CTASection'
import InstructorsCarousel from '@Features/LandingPage/Components/InstructorsCarousel'
import FindClassroomSection from '@Features/LandingPage/GuestLanding/Components/FindClassroomSection'
import { HomeBanners } from '@Features/LandingPage/StudentLanding/Components/HomeCards/HomeCards'
import FeatureStrip from '@Features/LandingPage/GuestLanding/Components/FeatureStrip'
import GuestCards from '@Features/LandingPage/GuestLanding/GuestCards'
import GuestLandingClassrooms from '@Features/LandingPage/GuestLanding/Components/GuestLandingClassrooms'
import Categories from '@Features/LandingPage/Components/Categories'
// parked with the stats band below — restore both together
// import Stats from '@Features/LandingPage/Components/Stats'
import BottomCta from '@Features/LandingPage/GuestLanding/Components/BottomCta'
import FeaturedCoursesCarousel from '@Features/LandingPage/Components/FeaturedCoursesCarousel'
import FeaturedBundlesCarousel from '@Features/Bundle/Components/FeaturedBundlesCarousel'
import GuestLandingCourses from '@Features/StandaloneCourse/Components/GuestLandingCourses'
import usePlatformFlags from '@Hooks/usePlatformFlags'

/**
 * Guest landing — the mobile-first design replica. Mobile (<1024): the design's
 * 512px centred column, hero = the marketing banner artwork. Desktop (≥1024):
 * the same banner full-bleed, then the same design sections inside the 1120px
 * column. The news-feed banners stay as they are.
 * Classroom sections gate on the classrooms platform; the featured-courses
 * carousel self-hides when the courses platform is off or nothing is featured.
 */
const GuestLanding: React.FC = () => {
	const { instructor_section, subjects_section } = useAppSelector(s => s.settings)
	const { classroomsEnabled } = usePlatformFlags()

	// `/` renders a completely different tree for authed students
	// (StudentLanding) — this event marks the GUEST acquisition surface only.
	useEffect(() => {
		trackEvent('ViewContent', { content_name: 'guest_landing', content_category: 'lander' })
	}, [])

	return (
		<StyledGuestLandingContainer>
			<DesktopOnly>
				<CTASection variant="desktop" />
			</DesktopOnly>
			<DesignColumn>
				<MobileOnly>
					<CTASection variant="mobile" />
				</MobileOnly>
				{instructor_section && <InstructorsCarousel variant="grid" />}
				{/* ONE mode-aware finder: classrooms / courses / hybrid→الكل */}
				<FindClassroomSection />
				{/* the SAME banner cards as the logged-in home (r-16 swipeable),
				    width-matched to the logged-in 960px column */}
				<BannersWrap>
					<HomeBanners />
				</BannersWrap>
				<FeatureStrip />
				<FeaturedCoursesCarousel />
				<FeaturedBundlesCarousel />
				{/* benefit cards are platform-generic marketing — always shown */}
				<GuestCards />
				{classroomsEnabled && <GuestLandingClassrooms />}
				{/* the catalog rail — the courses twin of الفصول المتاحة */}
				<GuestLandingCourses />
				{subjects_section && <Categories compact />}
				{/* Stats band (+كورس / +معلم / +اشتراكات) — parked, not deleted; the
				    component and its locale keys stay in place for when it returns.
				    <Stats variant="band" /> */}
				<BottomCta />
			</DesignColumn>
		</StyledGuestLandingContainer>
	)
}

const StyledGuestLandingContainer = styled.div`
	width: 100%;
	overflow-x: hidden; /* clip minor carousel-track bleed; section rails scroll internally */
`

/* The design shell: 512px centred column on mobile; desktop matches the
   logged-in pages' 960px column so guest and authed surfaces read the same. */
const DesignColumn = styled.div`
	width: 100%;
	max-width: 512px;
	margin: 0 auto;
	padding-bottom: 24px;
	display: flex;
	flex-direction: column;
	@media (min-width: 1024px) {
		max-width: 960px;
		padding: 16px 24px 48px;
	}
`
/* Same footprint the banners get on the logged-in home: the 960px shell width
   with the standard section side padding. */
const BannersWrap = styled.section`
	width: 100%;
	max-width: 960px;
	margin: 0 auto;
	padding: 12px 16px;
`
const DesktopOnly = styled.div`
	display: none;
	@media (min-width: 1024px) {
		display: block;
	}
`
const MobileOnly = styled.div`
	display: block;
	@media (min-width: 1024px) {
		display: none;
	}
`

export default GuestLanding
